import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Mail, ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast({
        title: 'Email required',
        description: 'Please enter your email address.',
        variant: 'destructive'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await forgotPassword(email);
      if (result.success) {
        toast({
          title: 'Password reset link sent',
          description: 'If an account exists with that email, a reset link has been sent.'
        });
        setIsSubmitted(true);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to send reset link. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Forgot Password</h1>
            <p className="text-gray-600">We'll send you a reset link</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center">Reset Your Password</CardTitle>
            </CardHeader>
            <CardContent>
              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <p className="text-sm text-gray-600">
                    Enter the email address associated with your account and we'll send you a link
                    to reset your password.
                  </p>

                  <div>
                    <Label htmlFor="email" className="text-gray-900">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      className="mt-2"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Mail size={18} className="mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>

                  <div className="text-center text-sm">
                    <Link to="/login" className="text-blue-600 hover:text-blue-700 inline-flex items-center">
                      <ArrowLeft size={16} className="mr-1" />
                      Back to Login
                    </Link>
                  </div>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Mail size={32} className="text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Check Your Email</h3>
                  <p className="text-gray-600">
                    If an account with that email exists, we've sent a password reset link to{' '}
                    <span className="font-medium">{email}</span>
                  </p>
                  <Link to="/login">
                    <Button className="bg-blue-600 hover:bg-blue-700 mt-4">
                      Return to Login
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;